// Shared financial calculator for Order Summary / Order Confirmation PDFs.
// Mirrors the math in src/components/crm/DealCalculator.tsx so the PDFs match
// the on-screen deal totals exactly.

export type OrderFinancials = ReturnType<typeof computeOrderFinancials>;

export interface DiscountRow {
  label: string;
  amount: number;
}

export const formatGBP = (amount: number): string =>
  (Number.isFinite(amount) ? amount : 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export function computeOrderFinancials(deal: any) {
  const dealData = (deal?.deal_data as any) || {};
  const orderType: string = dealData.order_type || 'cameras_and_credits';
  const paymentType: string = dealData.payment_structure || 'annual';
  const cameras: number = deal?.total_cameras || 1;
  const totalCreditsNeeded: number = deal?.total_images || 0;

  // DealCalculator stores credits_per_camera as ANNUAL credits per camera and
  // total_images as ANNUAL total credits (credits_per_camera * cameras).
  const creditsPerCamera: number = dealData.credits_per_camera || 0;
  const pricePerImage: number = Number(dealData.price_per_image ?? 0);
  const cameraPrice: number = Number(dealData.camera_unit_price ?? 0);
  const cameraCost: number = Number(dealData.camera_cost ?? cameras * cameraPrice);
  const accessoriesCost: number = Number(dealData.accessories_cost ?? 0);

  // Prefer stored plan metadata; fall back to detection from annual credits/camera.
  const storedPlan: string | undefined = dealData.plan;
  const storedCoverage: number | undefined = dealData.coverage_years;
  const detectPlan = (annualPerCamera: number) => {
    if (annualPerCamera >= 2920) return { name: 'Freedom', duration: 5, pricePerImage: 0.29 };
    if (annualPerCamera >= 2190) return { name: 'Professional', duration: 3, pricePerImage: 0.35 };
    if (annualPerCamera >= 1095) return { name: 'Extended', duration: 2, pricePerImage: 0.39 };
    return { name: 'Trial', duration: 1, pricePerImage: 0 };
  };
  const detected = detectPlan(creditsPerCamera);
  const planName = storedPlan || detected.name;
  const coverageYears = Number(storedCoverage ?? detected.duration) || 1;
  const currentPlan = { name: planName, duration: coverageYears, pricePerImage };
  const isTrial = planName === 'Trial';

  // Discount inputs.
  const rawTypes: string[] = Array.isArray(dealData.discount_types)
    ? dealData.discount_types
    : dealData.discount_type && dealData.discount_type !== 'none'
      ? [dealData.discount_type]
      : [];
  const has = (k: string) => rawTypes.includes(k);

  // Annual credits total (already annual, do NOT divide by duration).
  const annualCredits = totalCreditsNeeded;
  const annualCreditsGross = annualCredits * pricePerImage;
  // credits_cost is stored as annualCreditsCostNet (already net of image discount).
  const annualCreditsNet: number = Number(dealData.credits_cost ?? annualCreditsGross);
  const imageDiscountPerImage = isTrial
    ? 0
    : has('image_14p') ? 0.14
    : has('image_10p') ? 0.10
    : has('image_4p') ? 0.04
    : has('image_2p') ? 0.02
    : 0;
  const year1ImageDiscount = imageDiscountPerImage * annualCredits;
  const fullImageDiscount = year1ImageDiscount * coverageYears;

  const effectiveCameras = orderType === 'cameras_and_credits' ? cameras : 0;
  const cameraDiscountAmount = isTrial
    ? 0
    : has('camera_100') ? cameraCost
    : has('camera_50') ? cameraCost * 0.5
    : has('camera_10') ? effectiveCameras * 10
    : 0;
  const cameraDiscountLabel = has('camera_100')
    ? 'Camera Discount (100%)'
    : has('camera_50')
      ? 'Camera Discount (50%)'
      : has('camera_10')
        ? 'Camera Discount (£10/cam)'
        : '';

  const accessoriesDiscountAmount = has('accessories_100') ? accessoriesCost : 0;
  const trialDiscountAmount = isTrial && has('essential_100') ? cameraCost : 0;

  // Subtotals shown BEFORE discounts (gross).
  const camerasSubtotalGross = orderType === 'cameras_and_credits' ? cameraCost : 0;
  const creditsSubtotalGross = isTrial
    ? 0
    : paymentType === 'annual'
      ? annualCreditsGross
      : annualCreditsGross * coverageYears;
  const subtotalGross = camerasSubtotalGross + creditsSubtotalGross + accessoriesCost;

  // Year-1 / upfront totals (net) matching DealCalculator.
  const year1Total = isTrial
    ? cameraCost + accessoriesCost - trialDiscountAmount - accessoriesDiscountAmount
    : (cameraCost - cameraDiscountAmount)
      + annualCreditsNet
      + (accessoriesCost - accessoriesDiscountAmount);
  const upfrontTotal = isTrial
    ? cameraCost + accessoriesCost - trialDiscountAmount - accessoriesDiscountAmount
    : (cameraCost - cameraDiscountAmount)
      + annualCreditsNet * coverageYears
      + (accessoriesCost - accessoriesDiscountAmount);
  const finalTotal = isTrial
    ? year1Total
    : paymentType === 'annual'
      ? year1Total
      : upfrontTotal;
  const contractValue = isTrial
    ? finalTotal
    : paymentType === 'annual'
      ? year1Total + (coverageYears - 1) * annualCreditsNet
      : upfrontTotal;

  // Build the per-line discount rows to render (in display order).
  const discountRows: DiscountRow[] = [];
  if (cameraDiscountAmount > 0) discountRows.push({ label: cameraDiscountLabel, amount: cameraDiscountAmount });
  if (!isTrial) {
    // Image discount: for annual, show year-1 amount; for upfront, show full-contract amount.
    const imgAmount = paymentType === 'annual' ? year1ImageDiscount : fullImageDiscount;
    if (imgAmount > 0) {
      const per = imageDiscountPerImage.toFixed(2);
      const suffix = paymentType === 'annual'
        ? `Image Discount (£${per}/img × ${annualCredits.toLocaleString()})`
        : `Image Discount (£${per}/img × ${(annualCredits * coverageYears).toLocaleString()})`;
      discountRows.push({ label: suffix, amount: imgAmount });
    }
  }
  if (trialDiscountAmount > 0) discountRows.push({ label: 'Trial Plan Discount (100%)', amount: trialDiscountAmount });
  if (accessoriesDiscountAmount > 0) discountRows.push({ label: 'Accessories Discount (100%)', amount: accessoriesDiscountAmount });

  // Coverage / daily-cost stats used in the header strip.
  const totalDays = 365 * coverageYears;
  const shotsPerDay = Math.max(0, Math.floor(creditsPerCamera / 365));
  const imageCostPerDay = pricePerImage * shotsPerDay;
  const cameraCostPerDay = coverageYears > 0 ? cameraPrice / totalDays : 0;
  const averageCostPerDay = imageCostPerDay + cameraCostPerDay;

  return {
    // Inputs
    orderType,
    paymentType,
    cameras,
    totalCreditsNeeded,
    creditsPerCamera,
    pricePerImage,
    cameraPrice,
    cameraCost,
    accessoriesCost,
    dealAccessories: Array.isArray(dealData.accessories) ? dealData.accessories : [],
    cameraModel: dealData.camera_model || 'Day & Night Vision Camera SSC200',
    // Plan
    currentPlan,
    isTrial,
    coverageYears,
    // Credits
    annualCredits,
    annualCreditsGross,
    annualCreditsNet,
    // Discounts
    discountRows,
    cameraDiscountAmount,
    imageDiscountPerImage,
    year1ImageDiscount,
    fullImageDiscount,
    accessoriesDiscountAmount,
    trialDiscountAmount,
    // Totals
    subtotalGross,
    year1Total,
    upfrontTotal,
    finalTotal,
    contractValue,
    // Stats
    averageCostPerDay,
  };
}
